import { botInput, createBot, type Bot } from "../bot/bot";
import { buildRules, contentHash } from "../content/rules";
import { AudioSystem } from "../audio/audioSystem";
import { FxSystem } from "../fx/fxSystem";
import { Devices } from "../input/devices";
import { handshake } from "../net/handshake";
import { inputDelayFor, warmup } from "../net/warmup";
import { DEFAULT_ROLLBACK, RollbackSession } from "../net/rollback/session";
import { SignalClient } from "../net/signalClient";
import type { ServerMessage } from "../net/signalMessages";
import { connectPeer } from "../net/transport/webrtc";
import { laggy } from "../net/transport/laggy";
import { relayTransport } from "../net/transport/relay";
import type { Transport } from "../net/transport/transport";
import { Renderer } from "../render/renderer";
import { hashString } from "../shared/hash";
import { isSlowed, SLOW_RATE, type MatchRules } from "../sim";
import { DecisiveView } from "./decisiveView";
import { loadSettings } from "./settings";

export interface OnlineOptions {
  /** null — создать комнату; иначе войти в эту. */
  readonly room: string | null;
  readonly signalUrl: string;
  readonly bot: boolean;
  /** Имитация плохой сети на одном ПК: задержка в одну сторону, мс. */
  readonly lagMs: number;
  /** Своя задержка ввода, кадры (1–4) — только если задана в ссылке (?delay); иначе — по замеру пинга до раунда. */
  readonly inputDelay: number | null;
  /** Сразу запасной путь через сводник (?relay=1) — для проверки. */
  readonly relay: boolean;
  readonly onLink: (link: string | null) => void;
}

type Phase =
  | { readonly kind: "lobby"; readonly lines: string[] }
  | { readonly kind: "fight"; readonly session: RollbackSession }
  | { readonly kind: "error"; readonly message: string };

/**
 * Предзагрузка Chrome (страница открыта заранее, ещё не видна) не входит в комнату: ждём, пока страницу покажут.
 * Иначе вход в комнату занимает невидимая копия, а видимая вкладка получает «занята».
 */
function whenActivated(): Promise<void> {
  const page = document as Document & { prerendering?: boolean };
  if (!page.prerendering) return Promise.resolve();
  return new Promise((resolve) => {
    document.addEventListener(
      "prerenderingchange",
      () => {
        resolve();
      },
      { once: true },
    );
  });
}

/** Сид матча даёт создатель комнаты: случайный, но не из мира (в мире — только сидированный ГСЧ). */
const randomSeed = (): number => crypto.getRandomValues(new Uint32Array(1))[0] ?? 1;

/**
 * Бой онлайн по ссылке: сводник → WebRTC → знакомство → откат кадров. Каждый у себя, со своим геймпадом.
 * Правил боя здесь нет — они в мире; сеть — в src/net.
 */
export class OnlineMatch {
  private readonly rules: MatchRules = buildRules(["mannequin", "mannequin"], "foundry");
  private readonly fx = new FxSystem();
  /** Звук боя (audio/audioSystem.ts); включается первым жестом игрока. */
  private readonly audio = new AudioSystem();
  /** Решающий удар: замедление идёт в мире у обоих, здесь — его картинка (ракурс, облёт, вспышка). */
  private readonly decisive = new DecisiveView();
  private readonly devices: Devices;
  private readonly renderer: Renderer;
  private readonly settings = loadSettings(undefined);
  private readonly bot: Bot | null;
  private phase: Phase = { kind: "lobby", lines: ["ОНЛАЙН", "Связь со сводником…"] };
  private side: 0 | 1 = 0;
  /** Каким путём идут пакеты боя: напрямую (WebRTC) или через сводник. */
  private path: "webrtc" | "relay" | "" = "";
  /** Сводник: после соединения — для журнала шагов и «соперник отключился». */
  private signal: SignalClient | null = null;
  private fightFrames = 0;
  readonly content = hashString(`${contentHash()}|${__BUILD_ID__}`);

  constructor(
    canvas: HTMLCanvasElement,
    target: Window,
    private readonly options: OnlineOptions,
  ) {
    this.devices = new Devices(target);
    this.renderer = new Renderer(canvas);
    this.bot = options.bot ? createBot(options.room ? 77 : 33, this.rules) : null;
    void this.connect().catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      this.phase = { kind: "error", message };
      this.note(`ошибка: ${message}`);
      this.options.onLink(null);
    });
  }

  private lobby(...lines: string[]): void {
    this.phase = { kind: "lobby", lines: ["ОНЛАЙН", ...lines] };
    this.note(lines.join(" · "));
  }

  /** Шаг страницы — в журнал сводника (консультант 04.11.2026: матч должен быть виден целиком). */
  private note(text: string): void {
    this.signal?.send({ t: "note", text: `${this.side === 0 ? "создатель" : "гость"}: ${text}`.slice(0, 200) });
  }

  private async connect(): Promise<void> {
    await whenActivated();
    const signal = await SignalClient.open(this.options.signalUrl);
    this.signal = signal;
    const role = this.options.room ? "guest" : "host";
    this.side = role === "host" ? 0 : 1;
    let ice: ServerMessage & { t: "created" | "joined" };
    if (role === "host") {
      signal.send({ t: "create" });
      ice = await signal.wait(
        (message): message is ServerMessage & { t: "created" } => message.t === "created",
        10_000,
      );
      const link = `${window.location.origin}${window.location.pathname}?room=${ice.room}`;
      this.options.onLink(link);
      this.lobby("Пошлите ссылку сопернику", "Ждём соперника…");
      await signal.wait(
        (message): message is ServerMessage & { t: "peer-joined" } => message.t === "peer-joined",
        30 * 60_000,
      );
    } else {
      signal.send({ t: "join", room: this.options.room ?? "" });
      ice = await signal.wait((message): message is ServerMessage & { t: "joined" } => message.t === "joined", 10_000);
    }
    this.options.onLink(null);
    this.lobby("Соединяемся напрямую…");
    // напрямую по WebRTC; не встал (или ?relay=1) — через сводник
    this.path = "webrtc";
    let transport: Transport = this.options.relay
      ? relayTransport(signal)
      : await connectPeer(signal, role, ice.ice).catch(() => {
          this.lobby("Напрямую не вышло — идём через сводник…");
          this.path = "relay";
          return relayTransport(signal);
        });
    if (this.options.relay) this.path = "relay";
    if (this.options.lagMs > 0)
      transport = laggy(transport, this.options.lagMs, Math.round(this.options.lagMs / 6), 2, this.side + 1);
    signal.on((message) => {
      if (message.t === "peer-left" && this.phase.kind === "fight") {
        this.phase = { kind: "error", message: "Соперник отключился" };
        this.note("соперник отключился");
      }
    });
    this.lobby("Знакомимся…");
    const seed = await handshake(transport, role, this.content, randomSeed());
    // прогрев: раунд — только после замера связи, задержка ввода — по пингу, а не догоняет в бою
    this.lobby("Проверяем связь…");
    const link = await warmup(transport);
    const inputDelay = this.options.inputDelay ?? inputDelayFor(link);
    this.lobby(
      `Связь: пинг ${link.ping} мс, разброс ${link.jitter} мс`,
      `Путь: ${this.path === "relay" ? "через сводник" : "напрямую"} · задержка ввода ${inputDelay}`,
    );
    const session = new RollbackSession(this.rules, seed, this.side, transport, {
      ...DEFAULT_ROLLBACK,
      inputDelay,
    });
    session.onDesync = (frame) => {
      console.error(`Рассинхрон на кадре ${frame}`);
    };
    this.phase = { kind: "fight", session };
  }

  tick(): void {
    this.devices.poll();
    if (this.phase.kind !== "fight") return;
    if (!this.bot && !this.devices.slots[0]) {
      for (const device of this.devices.confirmedThisFrame()) this.devices.claim(device);
    }
    const session = this.phase.session;
    const bits = this.bot ? botInput(this.bot, session.state, this.side) : this.devices.bits(0);
    const { events } = session.tick(bits);
    this.decisive.record(session.state, events);
    // табло связи — в журнал сводника раз в 10 с
    if (++this.fightFrames % 600 === 0) this.note(this.status(session));
    this.fx.onEvents(events, session.state);
    this.audio.onStep(events, session.state);
    this.fx.update(isSlowed(session.state) ? 1 / SLOW_RATE : 1);
  }

  private status(session: RollbackSession): string {
    const stats = session.stats;
    const average = stats.rollbacks ? (stats.totalDepth / stats.rollbacks).toFixed(1) : "0";
    const path = this.path === "relay" ? "через сводник" : "напрямую";
    return `${path} · пинг ≈ ${Math.round(stats.rttFrames * 16.7)} мс · откат ${average} (макс ${stats.maxDepth}) · ожиданий ${stats.stalls} · рассинхронов ${stats.desyncs}`;
  }

  render(): void {
    const view = { flash: this.settings.flash / 100, shake: this.settings.shake / 100 };
    if (this.phase.kind === "fight") {
      const session = this.phase.session;
      const me = this.side === 0 ? "ВЫ" : "СОПЕРНИК";
      const other = this.side === 0 ? "СОПЕРНИК" : "ВЫ";
      const banner = !this.bot && !this.devices.slots[0] ? ["ВАШ ГЕЙМПАД", "Нажмите ✕ на своём геймпаде"] : [];
      const decisive = this.decisive.next(session.state);
      this.renderer.draw(decisive?.state ?? session.state, this.rules, this.fx, {
        ...(decisive ? { decisive } : {}),
        names: [me, other],
        viewer: this.side,
        banner,
        status: this.status(session),
        ...view,
      });
      return;
    }
    const lines = this.phase.kind === "lobby" ? this.phase.lines : ["ОНЛАЙН", this.phase.message];
    this.renderer.drawLobby(lines);
  }

  /** Для проверки двух браузеров: состояние связи. */
  /** Звук включается жестом игрока: клавиша, клик. */
  unlockAudio(): void {
    this.audio.unlock();
  }

  report(): {
    phase: string;
    frame: number;
    side: number;
    path: string;
    stats: RollbackSession["stats"] | null;
    error: string | null;
  } {
    const session = this.phase.kind === "fight" ? this.phase.session : null;
    return {
      phase: this.phase.kind,
      frame: session?.frame ?? 0,
      side: this.side,
      path: this.path,
      stats: session ? { ...session.stats } : null,
      error: this.phase.kind === "error" ? this.phase.message : null,
    };
  }
}
